import React, { FC, createContext, useContext, useEffect, useState } from 'react';
import { GlobalStyles, useTheme } from '@mui/material';
import 'intro.js/introjs.css';
import { useNavigate } from 'react-router-dom';
import URLS from '../enums/route';
import useDataPermission from '../hooks/useDataPermission';
import { usePermissionStore } from '../store/permission.store';
import zIndex from '@mui/material/styles/zIndex';

// encode custom SVG icons
const mySkipSvg = encodeURIComponent(`
<svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
  <path d="M11 1L1 11M1 1L11 11" stroke="#546D79" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`);
const myActiveDotSvg = encodeURIComponent(`
<svg width="8" height="8" viewBox="0 0 8 8" xmlns="http://www.w3.org/2000/svg">
  <rect width="8" height="8" rx="4" fill="#8EA6B2" />
</svg>
`);
const myInactiveDotSvg = encodeURIComponent(`
<svg width="8" height="8" viewBox="0 0 8 8" xmlns="http://www.w3.org/2000/svg">
  <rect width="8" height="8" rx="4" fill="#879198" fill-opacity="0.3" />
</svg>
`);

// --- Context Definition ---
interface TourContextType {
  enabledHome: boolean;
  enabledNotification: boolean;
  enabledCatalog: boolean;
  enableService: boolean;
  onExitHome: () => void;
  onExitNotification: () => void;
  onExitCatalog: () => void;
  onExitService: () => void;
  SaveRead: (tour: "service" | "catalog" | "home" | "notification" | "bussnies") => void;
  ResetTour: () => void;
  setEnabledHome: (val: boolean) => void;
  setEnabledNotification: (val: boolean) => void;
  setEnabledCatalog: (val: boolean) => void;
  setEnableService: (val: boolean) => void;
  catalogNumber: number;
  setCatalogNumber: (val: number) => void;
  onExitBussnies: () => void;
  setEnabledBussnies: (val: boolean) => void;
  openCreateUserModal: boolean
  setOpenCreateUserModal: (val: boolean) => void
  bussniesNumber: number; 
  setBussniesNumber: (val: number) => void;
}
const TourContext = createContext<TourContextType | null>(null);
export const useTour = (): TourContextType => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be inside TourProvider');
  return ctx;
};

export const TourProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const {data} = useDataPermission()
  const {setChoosedPermission,setOpenEdit} = usePermissionStore()
  const [enabledHome, setEnabledHome] = useState(false);
  const [enabledNotification, setEnabledNotification] = useState(false);
  const [enabledCatalog, setEnabledCatalog] = useState(false);
  const [enableService, setEnableService] = useState(false);
  const [catalogNumber, setCatalogNumber] = useState(0);
  const [enabledBussnies, setEnabledBussnies] = useState(false)
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false)
  const [bussniesNumber, setBussniesNumber] = useState(0)
  const [force, setForce] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate()
  const onExitHome = () => { 
    setEnabledHome(false); 
    SaveRead('home');  
    if(force){
      navigate(URLS.NOTIFICATIONS.LINK)
    }
  };
  const onExitNotification = () => { 
    setEnabledNotification(false); 
    SaveRead('notification'); 
    if(force){
      navigate(URLS.CATALOG.LINK)
    }
  };
  const onExitCatalog = () => { 
    setEnabledCatalog(false); 
    SaveRead('catalog'); 
    if(force){
      navigate(URLS.SERVICE.LINK)
    }
  };
  const onExitService = () => { 
    setEnableService(false); 
    SaveRead('service'); 
    if(force){
      navigate(URLS.BUSSNIES.LINK)
    }
  };
  const onExitBussnies = () => { 
    setEnabledBussnies(false); 
    SaveRead('bussnies'); 
    setForce(false)
  };

  const SaveRead = (tour: 'service' | 'notification' | 'catalog' | 'home' | "bussnies") => {
    localStorage.setItem(tour, 'true');
  };

  const ResetTour = () => {
    setBussniesNumber(0)
    setForce(true)
    localStorage.removeItem('bussnies');
    localStorage.removeItem('service');
    localStorage.removeItem('catalog');
    localStorage.removeItem('home');
    localStorage.removeItem('notification');
  };

  const anyTourRunning = enabledHome || enabledNotification || enabledCatalog || enableService || enabledBussnies;

  useEffect(() => {
    if(bussniesNumber === 2){
        setOpenEdit(true)
        setChoosedPermission({
          "id": 2,
          "isEditable": false,
          "title": "אדמין",
          "color": "#F58E08",
          "permissionDetaileds": [
              {
                  "id": 2,
                  "isArchitecture": false,
                  "isOrderable": true,
                  "isOrderEditable": true,
                  "isServiceable": true,
                  "isServiceEditable": true,
                  "isOrdersView": true,
                  "isServiceView": true,
                  "isDeliveryView": true,
                  "isAccounting": true
              }
          ]
      })
    } else {
        setOpenEdit(false)
        setChoosedPermission(null)
    }
},[bussniesNumber])

  return (
    <TourContext.Provider
      value={{
        enabledHome,
        enabledNotification,
        enabledCatalog,
        enableService,
        onExitHome,
        onExitNotification,
        onExitCatalog,
        onExitService,
        SaveRead,
        ResetTour,
        setEnabledHome,
        setEnabledNotification,
        setEnabledCatalog,
        setEnableService,
        catalogNumber,
        setCatalogNumber,
        onExitBussnies,
        setEnabledBussnies,
        openCreateUserModal,
        setOpenCreateUserModal,
        bussniesNumber,
        setBussniesNumber
      }}
    >
      {children}

      {anyTourRunning && (
        <GlobalStyles styles={{
          'html, body': {
            overflow: 'hidden !important',
            height: '100% !important',
          },
        }} />
      )}

      <GlobalStyles styles={{
        '.introjs-overlay': {
          boxShadow: '0 0 0 5000px rgba(17, 70, 96, 0.4) !important',
          pointerEvents: 'all !important',
        },
        '.introjs-helperLayer': {
          WebkitBoxShadow: '0 0 0 5000px rgba(17, 70, 96, 0.4) !important',
          boxShadow: '0 0 0 5000px rgba(17, 70, 96, 0.4) !important',
          pointerEvents: 'none !important',
        },
        '.introjs-helperLayer.home1-helper-rounded': {
          borderTopRightRadius: '38px !important',
          borderTopLeftRadius: '38px !important',
          marginTop: '5px !important',
        },
        '.introjs-tooltip': {
          width: '85vw !important',
          maxWidth: 'none !important',
          left: '50% !important',
          transform: 'translateX(-50%) !important',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: '15px',
          padding: theme.spacing(2),
          boxShadow: theme.shadows[4],
        },
        '.introjs-tooltip-header': {
          padding: '0px !important',
        },
        '.introjs-skipbutton': {
          left: 'auto !important',
          right: '0 !important',
          textIndent: '-9999px',
          border: 'none !important',
          backgroundImage: `url("data:image/svg+xml;utf8,${mySkipSvg}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          width: theme.spacing(3),
          height: theme.spacing(3),
          '&:focus': { outline: 'none' },
        },
        '.introjs-tooltip-title': {
          fontWeight: 700,
          fontSize: '20px',
          color: '#2F4B6C',
        },
        '.introjs-tooltiptext': { padding: '0px' },
        '.introjs-tooltipArrow': { color: theme.palette.background.paper },
        '.introjs-button': {
          width: '100%',
          padding: '0 8px !important',
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          border: 'none',
          '&:focus': { outline: 'none !important', boxShadow: 'none !important', border: 'none !important', backgroundColor: 'white !important' },
          '&:active': { outline: 'none !important', boxShadow: 'none !important', backgroundColor: 'white !important', border: 'none !important' },
        },
        ".introjs-showElement": {
          zIndex: bussniesNumber === 2 
            ? "1 !important" 
            : "9999999 !important",
        },
        '.introjs-tooltipbuttons': { width: '100%', display: 'flex', justifyContent: 'space-between', borderTop: 'none', padding: '0px !important'},
        '.myIntoText': { fontSize: '15px', fontWeight: 400, color: '#546D79' },
        '.introjs-bullets': { display: 'flex', justifyContent: 'center', margin: `${theme.spacing(2)} 0 !important`, padding: 0, listStyle: 'none !important' },
        '.introjs-bullets li': { margin: '0 4px' },
        '.introjs-bullets li a': { display: 'block !important', width: '8px !important', height: '8px !important', padding: 0, backgroundImage: `url("data:image/svg+xml;utf8,${myInactiveDotSvg}") !important`, backgroundRepeat: 'no-repeat !important', backgroundPosition: 'center !important', backgroundColor: 'transparent !important' },
        '.introjs-bullets li a.active': { backgroundImage: `url("data:image/svg+xml;utf8,${myActiveDotSvg}") !important` },
        '.introjs-tooltip.introjs-top-left': { transform: 'translate(-52%,2%) !important' },
        '.catalog2intro': { transform: 'translate(-92%,0%) !important' },
        '.introjs-tooltip.catalog3intro': { left: '40% !important', transform: 'translateX(-50%) !important', maxWidth: '90vw !important', boxSizing: 'border-box' },
        '.introjs-helperLayer.service2-rounded': {
  
        },
        
      }} />
    </TourContext.Provider>
  );
};
